// HTN standalone equipment persistence.
//
// The /play/hypertension standalone runs on a THROWAWAY anonymous user. Map/run
// progress is keyed on the stable demo slug in localStorage, so it survives the
// anon user being swapped (2h demo-token expiry, the 7-day janitor reap, or a
// mid-page 401 re-mint). Equipped drugs, however, live server-side keyed on the
// anon user id (equipment_state.user_id) — so when the user is swapped, the map
// survives but the regimen is stranded on the dead user. This module gives the
// equipped regimen the SAME device-local durability tier as the map: a versioned
// snapshot under the same stable owner, replayed onto whatever anon user is
// currently active. Combat stays server-derived from equipment_state at execution
// time; this is purely the persistence/rehydrate layer.
//
// Intentionally self-contained (no import of rankableElements) so the low-level
// api/client.js can pull the slug-read helper without dragging combatData /
// keystoneCards into the core client chunk. htnEquipmentPersistence.test.js
// asserts this list stays in lockstep with HTN_REGIMEN_DRUGS.

export const HTN_EQUIPMENT_STORAGE_VERSION = 1;

// The full first-line-through-reward antihypertensive regimen allow-list. Mirror
// of data/rankableElements.js HTN_REGIMEN_DRUGS (slug column). Used to validate
// what we persist/replay so a generic-anon random loadout can never leak in.
export const HTN_REGIMEN_DRUG_SLUGS = [
  'lisinopril', 'enalapril', 'benazepril',
  'losartan', 'valsartan', 'olmesartan',
  'amlodipine', 'nifedipine', 'felodipine', 'verapamil', 'diltiazem',
  'metoprolol', 'atenolol', 'bisoprolol', 'carvedilol', 'nebivolol', 'labetalol',
  'hydrochlorothiazide', 'chlorthalidone', 'indapamide',
  'furosemide', 'torsemide', 'bumetanide',
  'spironolactone', 'eplerenone', 'amiloride', 'triamterene',
  'acetazolamide', 'mannitol',
];

const HTN_REGIMEN_DRUG_SLUG_SET = new Set(HTN_REGIMEN_DRUG_SLUGS);

export function htnEquipmentStorageKey(owner) {
  return owner ? `htn_equipment:${owner}` : null;
}

// Normalize an arbitrary slug list to canonical, allow-listed, deduped HTN
// regimen slugs, preserving first-seen order.
export function filterHtnRegimenSlugs(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    const slug = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (!slug || seen.has(slug) || !HTN_REGIMEN_DRUG_SLUG_SET.has(slug)) continue;
    seen.add(slug);
    out.push(slug);
  }
  return out;
}

// Extract the equipped HTN regimen slugs from a /api/skill-tree/player-state
// response (equipped_drugs is the active-shelf regimen).
export function htnEquippedSlugsFromState(state) {
  return filterHtnRegimenSlugs(state?.equipped_drugs);
}

// Read the persisted regimen snapshot for an owner. Returns a validated slug
// array (empty on miss / bad JSON / version mismatch / garbage payload).
export function readHtnEquipmentSlugs(owner) {
  const key = htnEquipmentStorageKey(owner);
  if (!key || typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || 'null');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    if (parsed.version !== HTN_EQUIPMENT_STORAGE_VERSION) return [];
    return filterHtnRegimenSlugs(parsed.equipped_drug_slugs);
  } catch {
    return [];
  }
}

// Write the regimen snapshot for an owner. An explicit empty array is a valid,
// meaningful snapshot (an intentional bare regimen) and is persisted as such.
export function writeHtnEquipmentSlugs(owner, slugs) {
  const key = htnEquipmentStorageKey(owner);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify({
      version: HTN_EQUIPMENT_STORAGE_VERSION,
      equipped_drug_slugs: filterHtnRegimenSlugs(slugs),
      updated_at: Date.now(),
    }));
  } catch (_) { /* best-effort — storage disabled */ }
}

export function clearHtnEquipment(owner) {
  const key = htnEquipmentStorageKey(owner);
  if (!key || typeof window === 'undefined') return;
  try { window.localStorage.removeItem(key); } catch (_) { /* best-effort */ }
}
